import { Building2, Clock3, ExternalLink, Info, LogIn } from 'lucide-react';
import { GRADIENT_BRAND, RADIUS, SHADOW } from '@parsina/framework/tokens';
import { C, soft } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import JoinClinicPanel from './JoinClinicPanel.jsx';
import { CLINIC_APP_URL } from '../lib/runtimeEnv';

// «هنوز به هیچ مطب یا کلینیکی وصل نیستید» — what پذیرش says instead of
// «نوبتی برای این روز ثبت نشده» when the doctor belongs to no clinic at all.
//
// It replaces the empty state rather than sitting beside it because the two
// statements cannot both be true in a useful way: "no appointments today" is a
// fact about the day, and for a doctor with no clinic it will be true every day
// forever. The honest thing to say is why.
//
// It is a GUIDE, not a menu of two nouns: each path is the numbered sequence of
// what to actually do, because a doctor who has never joined a clinic does not
// know that the code comes from the clinic manager's «همکاران» screen, and a
// doctor who wants their own practice does not know that practices are defined
// in the clinic panel rather than here.

const CLINIC_ONBOARDING_URL = `${CLINIC_APP_URL}/onboarding`;

/** One numbered step. `children` is whatever the step is actually done with. */
const Step = ({ n, title, hint, children, compact }) => {
  const { formatNumber } = useTranslation();
  return (
    <li style={{
      display: 'grid',
      gridTemplateColumns: `${compact ? 24 : 26}px 1fr`,
      columnGap: 10, rowGap: 8, alignItems: 'start',
    }}>
      <span
        aria-hidden
        style={{
          width: compact ? 24 : 26, height: compact ? 24 : 26, borderRadius: '50%',
          display: 'grid', placeItems: 'center', marginTop: 2,
          background: soft(C.navy, 0.1), color: C.navy,
          fontSize: '0.71875rem', fontWeight: 800,
        }}
      >
        {formatNumber(n)}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: compact ? '0.8125rem' : '0.84375rem', fontWeight: 700, lineHeight: 1.6, color: C.textPrimary }}>{title}</div>
        {hint && <div style={{ fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.75, marginTop: 1 }}>{hint}</div>}
      </div>
      {children && <div style={{ gridColumn: 2, minWidth: 0 }}>{children}</div>}
    </li>
  );
};

const Section = ({ icon, tint, title, who, children, compact }) => (
  <section style={{
    border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
    padding: compact ? '14px 14px 12px' : '18px 18px 16px',
    display: 'flex', flexDirection: 'column', gap: compact ? 12 : 14,
    background: C.card,
  }}>
    <header style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
      <span
        aria-hidden
        style={{
          width: compact ? 36 : 40, height: compact ? 36 : 40, borderRadius: RADIUS.control,
          display: 'grid', placeItems: 'center', flexShrink: 0,
          background: soft(tint, 0.12), color: tint,
        }}
      >
        {icon}
      </span>
      <h3 style={{ margin: 0, fontSize: compact ? '0.875rem' : '0.90625rem', fontWeight: 800, lineHeight: 1.4, color: C.textPrimary }}>
        {title}
        <span style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: C.textSecondary, marginTop: 1 }}>{who}</span>
      </h3>
    </header>
    <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: compact ? 10 : 12 }}>
      {children}
    </ol>
  </section>
);

/**
 * The pending state: a request has been sent and the clinic has not answered.
 *
 * Distinct from the guide on purpose — telling a doctor who already sent a code
 * to "enter a code" is the fastest way to get a second, duplicate request.
 */
const PendingCard = ({ pending, compact }) => {
  const { t } = useTranslation();
  const first = pending[0];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        border: `1px solid ${C.border}`, borderRadius: RADIUS.card,
        padding: compact ? '12px 14px' : '14px 16px',
      }}>
        <span
          aria-hidden
          style={{
            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
            display: 'grid', placeItems: 'center',
            background: GRADIENT_BRAND, color: '#fff', fontWeight: 800,
          }}
        >
          {(first?.name || t('common.punct.question')).trim().charAt(0)}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.84375rem', fontWeight: 800, color: C.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {first?.name || t('common.labels.clinic')}
          </div>
          {first?.city && <div style={{ fontSize: '0.75rem', color: C.textSecondary }}>{first.city}</div>}
        </div>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0,
          padding: '4px 10px', borderRadius: 999,
          background: soft(C.amber, 0.14), color: C.amber,
          fontSize: '0.71875rem', fontWeight: 700, whiteSpace: 'nowrap',
        }}>
          <Clock3 size={13} aria-hidden />
          {t('clinic.noClinic.pending.badge')}
        </span>
      </div>
      <p style={{ margin: 0, fontSize: '0.78125rem', color: C.textSecondary, lineHeight: 1.8 }}>
        {t('clinic.noClinic.pending.hint')}
      </p>
    </div>
  );
};

/**
 * `variant` picks the geometry, never the content: the mobile surface is the
 * same guide with the two sections stacked and the controls full-width
 * (DESIGN-CONCEPT-MOBILE §6), not a shortened one. A doctor on a phone needs
 * the same three steps — they just read them in one column.
 */
const NoClinicGuide = ({ variant = 'web', pending = [], onJoined, 'data-testid': testId = 'reception-no-clinic' }) => {
  const { t } = useTranslation();
  const compact = variant === 'mobile';
  const hasPending = pending.length > 0;

  return (
    <div
      data-testid={testId}
      data-state={hasPending ? 'pending' : 'guide'}
      style={{
        background: C.card,
        borderRadius: compact ? RADIUS.card : RADIUS.hero,
        boxShadow: SHADOW.card,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* The band is the clinic panel's AuthShell band, so a doctor who has just
          come from /start or /invite meets the same chrome here. */}
      <div style={{
        background: GRADIENT_BRAND, color: '#fff',
        padding: compact ? '14px 16px' : '18px 24px',
        display: 'flex', gap: 14, alignItems: 'center',
      }}>
        <span
          aria-hidden
          style={{
            width: 36, height: 36, borderRadius: RADIUS.control, flexShrink: 0,
            background: 'rgba(255,255,255,0.18)', display: 'grid', placeItems: 'center',
          }}
        >
          <Building2 size={19} />
        </span>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: compact ? '0.9375rem' : '1.0625rem', fontWeight: 800, lineHeight: 1.45 }}>
            {hasPending ? t('clinic.noClinic.pending.title') : t('clinic.noClinic.title')}
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.7)' }}>
            {hasPending ? t('clinic.noClinic.pending.sub') : t('clinic.noClinic.sub')}
          </p>
        </div>
      </div>

      <div style={{
        padding: compact ? '14px 14px 16px' : '20px 24px 24px',
        display: 'flex', flexDirection: 'column', gap: compact ? 12 : 16,
      }}>
        {hasPending ? (
          <PendingCard pending={pending} compact={compact} />
        ) : (
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: compact ? 12 : 16,
              alignItems: 'stretch',
            }}>
              <Section
                compact={compact}
                tint={C.navy}
                icon={<LogIn size={compact ? 18 : 20} />}
                title={t('clinic.noClinic.join.title')}
                who={t('clinic.noClinic.join.who')}
              >
                <Step compact={compact} n={1} title={t('clinic.noClinic.join.s1')} hint={t('clinic.noClinic.join.s1Hint')} />
                <Step compact={compact} n={2} title={t('clinic.noClinic.join.s2')}>
                  {/* The real thing, not a picture of it: the same panel the
                      settings «مطب‌ها» section uses, so the code, the QR scanner
                      and the error messages are one implementation. */}
                  <JoinClinicPanel showTitle={false} onJoined={onJoined} />
                </Step>
                <Step compact={compact} n={3} title={t('clinic.noClinic.join.s3')} hint={t('clinic.noClinic.join.s3Hint')} />
              </Section>

              <Section
                compact={compact}
                tint={C.teal}
                icon={<Building2 size={compact ? 18 : 20} />}
                title={t('clinic.noClinic.own.title')}
                who={t('clinic.noClinic.own.who')}
              >
                <Step compact={compact} n={1} title={t('clinic.noClinic.own.s1')} hint={t('clinic.noClinic.own.s1Hint')}>
                  {/* Same tab, same session: the clinic panel signs in silently
                      off the shared Keycloak session, and a popup-blocked new
                      tab would look like a dead button. */}
                  <a
                    href={CLINIC_ONBOARDING_URL}
                    data-testid={`${testId}-create`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      width: compact ? '100%' : 'auto',
                      height: 44, padding: '0 18px', borderRadius: RADIUS.control,
                      background: GRADIENT_BRAND, color: '#fff',
                      fontSize: '0.84375rem', fontWeight: 800, textDecoration: 'none',
                      boxShadow: `0 6px 16px ${soft(C.teal, 0.28)}`,
                    }}
                  >
                    {t('clinic.noClinic.own.cta')}
                    <ExternalLink size={15} aria-hidden />
                  </a>
                </Step>
                <Step compact={compact} n={2} title={t('clinic.noClinic.own.s2')} hint={t('clinic.noClinic.own.s2Hint')} />
                <Step compact={compact} n={3} title={t('clinic.noClinic.own.s3')} hint={t('clinic.noClinic.own.s3Hint')} />
              </Section>
            </div>

            <div
              role="note"
              style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
                borderRadius: RADIUS.control, padding: '10px 12px',
                background: soft(C.navy, 0.06), color: C.navyDark,
                fontSize: '0.78125rem', lineHeight: 1.7,
              }}
            >
              <Info size={15} aria-hidden style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{t('clinic.noClinic.foot')}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NoClinicGuide;
